
import React, { useState, useMemo } from 'react';
import { 
  History, 
  Search, 
  CalendarDays,
  User as UserIcon, 
  Trello, 
  FileText, 
  Edit3, 
  PlusCircle, 
  CheckCircle2,
  Trash2,
  MessageSquare,
  Clock,
  RotateCcw
} from 'lucide-react';
import { Task, Document, User, AuditEvent } from '../types';

interface HistoryLogProps {
  tasks: Task[];
  letters: Document[];
  users: User[];
  currentUser: User;
  auditLogs?: AuditEvent[];
  onRestore: (type: 'CRM' | 'Tarefa' | 'Ofício' | 'Documento' | 'Atendimento', id: string) => void;
}

type UnifiedHistoryItem = {
  id: string;
  userId: string;
  timestamp: string;
  action: string;
  entityType: 'Tarefa' | 'Ofício' | 'Documento' | 'Sistema' | 'CRM' | 'Atendimento';
  entityName: string;
  entityId: string;
};

type TimeFilter = 7 | 14 | 30 | 90 | 'all';

const HistoryLog: React.FC<HistoryLogProps> = ({ tasks, letters, users, currentUser, auditLogs = [], onRestore }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');

  const allKnownUsers = useMemo(() => {
    const list = [...users];
    if (!list.find(u => u.id === currentUser.id)) {
      list.push(currentUser);
    }
    return list;
  }, [users, currentUser]);

  const unifiedHistory = useMemo(() => {
    // 1. Histórico de Tarefas (Ativas e Inativas)
    const taskHistories: UnifiedHistoryItem[] = tasks.flatMap(task => 
      (task.history || []).map(h => ({ 
        id: h.id,
        userId: h.userId,
        timestamp: h.timestamp,
        action: h.action,
        entityType: 'Tarefa', 
        entityName: task.title,
        entityId: task.id
      }))
    );

    // 2. Histórico de Documentos (Ativos e Inativos)
    const letterHistories: UnifiedHistoryItem[] = letters.flatMap(letter => 
      (letter.history || []).map(h => ({ 
        id: h.id,
        userId: h.userId,
        timestamp: h.timestamp,
        action: h.action,
        entityType: 'Documento', 
        entityName: letter.subject,
        entityId: letter.id
      }))
    );

    // 3. Logs de Auditoria (Sistema - Exclusões/Restaurações)
    const systemHistories: UnifiedHistoryItem[] = auditLogs.map(log => ({
      id: log.id,
      userId: log.user_id,
      timestamp: log.timestamp,
      action: log.action,
      entityType: log.entity_type === 'Ofício' ? 'Documento' : log.entity_type as any, // Migra logs antigos visualmente
      entityName: log.entity_name,
      entityId: log.entity_id || 'unknown'
    }));

    let result = [...taskHistories, ...letterHistories, ...systemHistories]
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    // Filtro de Tempo
    if (timeFilter !== 'all') {
      const now = new Date();
      const limitDate = new Date();
      limitDate.setDate(now.getDate() - (timeFilter as number));
      result = result.filter(h => new Date(h.timestamp) >= limitDate);
    }

    // Filtro de Busca
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(h => {
        const user = allKnownUsers.find(u => u.id === h.userId);
        return (
          h.entityName.toLowerCase().includes(s) || 
          h.action.toLowerCase().includes(s) ||
          (user?.name && user.name.toLowerCase().includes(s))
        );
      });
    }

    return result;
  }, [tasks, letters, auditLogs, searchTerm, timeFilter, allKnownUsers]);

  const getActionIcon = (action: string) => {
    const a = action.toLowerCase();
    if (a.includes('lixeira') || a.includes('excluiu')) return <Trash2 size={16} className="text-red-500" />;
    if (a.includes('restaurou')) return <RotateCcw size={16} className="text-emerald-500" />;
    if (a.includes('criou') || a.includes('iniciou')) return <PlusCircle size={16} className="text-emerald-500" />;
    if (a.includes('concluiu')) return <CheckCircle2 size={16} className="text-emerald-500" />;
    if (a.includes('arquivada')) return <History size={16} className="text-amber-500" />;
    if (a.includes('comentário')) return <MessageSquare size={16} className="text-blue-500" />;
    return <Edit3 size={16} className="text-slate-400" />;
  };

  const isDeletableAction = (action: string) => action.toLowerCase().includes('moveu') && action.toLowerCase().includes('lixeira');

  const canUndo = (item: UnifiedHistoryItem) => {
    if (!isDeletableAction(item.action)) return false;
    return true; 
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-5xl font-black text-slate-900 dark:text-white tracking-tighter">Histórico Geral</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-2 font-medium">Rastreabilidade e auditoria do gabinete.</p>
        </div>
      </div>

      <div className="bg-white dark:bg-navy-900 rounded-[3rem] border border-slate-200 dark:border-navy-800 shadow-sm overflow-hidden flex flex-col">
        {/* Filtros */}
        <div className="p-8 border-b border-slate-100 dark:border-navy-800 bg-slate-50/50 dark:bg-navy-950/30 space-y-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
             <div className="relative flex-1">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300" size={22} />
                <input 
                  type="text" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por assessor, tarefa ou ação..." 
                  className="pl-14 pr-6 py-4 bg-white dark:bg-navy-900 border border-slate-200 dark:border-navy-700 rounded-2xl w-full text-sm font-bold focus:ring-4 focus:ring-black/5 dark:focus:ring-white/5 outline-none transition-all shadow-sm dark:text-white"
                />
             </div>
             
             <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mr-2 flex items-center gap-2">
                  <CalendarDays size={14} /> Período:
                </span>
                {[7, 14, 30, 90, 'all'].map((val) => (
                  <button
                    key={val}
                    onClick={() => setTimeFilter(val as TimeFilter)}
                    className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                      timeFilter === val 
                      ? 'bg-black dark:bg-white text-white dark:text-black shadow-lg shadow-black/20' 
                      : 'bg-white dark:bg-navy-900 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-navy-700 hover:border-black dark:hover:border-white hover:text-black dark:hover:text-white'
                    }`}
                  >
                    {val === 'all' ? 'Tudo' : `${val}d`}
                  </button>
                ))}
             </div>
          </div>
        </div>

        {/* Lista de Auditoria */}
        <div className="flex-1 overflow-y-auto max-h-[65vh] custom-scrollbar">
          {unifiedHistory.length > 0 ? (
            <div className="divide-y divide-slate-100 dark:divide-navy-800">
              {unifiedHistory.map((item) => {
                const user = allKnownUsers.find(u => u.id === item.userId);
                return (
                  <div key={item.id} className="flex items-center gap-8 p-8 hover:bg-neutral-50 dark:hover:bg-navy-950/50 transition-all group border-l-4 border-transparent hover:border-black dark:hover:border-white relative">
                    <div className="bg-white dark:bg-navy-800 p-4 rounded-2xl shadow-sm border border-slate-100 dark:border-navy-700 group-hover:bg-black group-hover:text-white dark:group-hover:bg-white dark:group-hover:text-black transition-all">
                      {getActionIcon(item.action)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                         <span className="text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full border bg-slate-50 text-slate-600 border-slate-100">
                           {item.entityType}
                         </span>
                         <span className="text-slate-300">/</span>
                         <span className={`text-[11px] font-black truncate ${item.action.includes('lixeira') ? 'text-red-500 line-through' : 'text-slate-800 dark:text-slate-200'}`}>{item.entityName}</span>
                      </div>
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-400 leading-relaxed">
                        <span className="font-black text-slate-900 dark:text-white">{user?.name || 'Sistema'}</span> {item.action} 
                      </p>
                    </div>

                    <div className="flex items-center gap-10 shrink-0">
                       {canUndo(item) && (
                         <button 
                           onClick={() => onRestore(item.entityType as any, item.entityId)}
                           className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-600 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-emerald-100 transition-all"
                         >
                           <RotateCcw size={12} /> Desfazer
                         </button>
                       )}
                       <div className="text-right">
                          <p className="text-xs font-black text-slate-900 dark:text-white">{new Date(item.timestamp).toLocaleDateString('pt-BR')}</p>
                          <p className="text-[10px] text-slate-400 font-black uppercase mt-1 tracking-widest">
                            {new Date(item.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </p>
                       </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-40 text-center space-y-4 opacity-30">
              <History size={64} className="mx-auto dark:text-white" />
              <p className="text-lg font-black uppercase tracking-widest dark:text-white">Nenhuma atividade encontrada</p>
            </div>
          )}
        </div>
        
        <div className="p-8 bg-slate-50 dark:bg-navy-950/30 border-t border-slate-100 dark:border-navy-800 flex items-center justify-between">
           <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
              <Clock size={16} /> Audit log atualizado em tempo real
           </div>
           <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
             {unifiedHistory.length} Registros no período selecionado
           </p>
        </div>
      </div>
    </div>
  );
};

export default HistoryLog;
